import { useEffect, useCallback, useState, useRef } from 'react';
import { logPopoverPosition } from '@/debug/popoverPositionDebug';

export interface PopoverPositionChangePayload {
  position: string;
  left: number;
  top: number;
  triggerRect: DOMRect;
  popoverRect: { width: number; height: number };
}

export interface UsePopoverPositioningOptions {
  /** Ref к триггеру. Не нужен, если передан anchorEl */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /** Прямая ссылка на якорный элемент (приоритет над triggerRef.current) */
  anchorEl?: HTMLElement | null;
  popoverRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  preferredPositionX?: 'left' | 'right';
  preferredPositionY?: 'top' | 'bottom';
  /**
   * Горизонтальное выравнивание поповера относительно триггера при позиции top/bottom.
   * 'center' — центр поповера совпадает с центром триггера (по умолчанию)
   * 'start'  — левый край поповера совпадает с левым краем триггера
   * 'end'    — правый край поповера совпадает с правым краем триггера
   */
  horizontalAlign?: 'center' | 'start' | 'end';
  /**
   * Вертикальное выравнивание поповера относительно триггера при позиции left/right.
   * 'center' — центр поповера совпадает с центром триггера (по умолчанию)
   * 'start'  — верхний край поповера совпадает с верхним краем триггера
   * 'end'    — нижний край поповера совпадает с нижним краем триггера
   */
  verticalAlign?: 'center' | 'start' | 'end';
  offset?: number;
  margin?: Partial<{ top: number; bottom: number; left: number; right: number }>;
  onPositionChange?: (payload: PopoverPositionChangePayload) => void;
  dependencies?: unknown[];
  priorityAxis?: 'x' | 'y';
  zIndex?: number;
  /** Смещение по вертикали (px). Отрицательное — подменю выше, для выравнивания по строке. */
  verticalOffset?: number;
  /**
   * После первого размещения с clamp в viewport запоминается фактическое смещение попапа относительно якоря.
   * При прокрутке попап только транслируется вместе с кнопкой (дельта getBoundingClientRect якоря),
   * без нового clamp к viewport — нет скачка и попап остаётся «привязанным» к кнопке.
   * При resize — полный пересчёт с clamp и обновление якорной привязки.
   */
  clampOnScrollFollow?: boolean;
  /**
   * Якорь исчез из документа (строка таблицы снята виртуализатором и т.п.): закрыть попап вместо «прилипания» к вьюпорту.
   */
  onAnchorDetached?: () => void;
  /**
   * ResizeObserver на самом попапе: при смене внутреннего контента вызывается clamp-позиционирование.
   * В фильтрах с длинным чеклистом микросдвиги размера дают ложные срабатывания — отключайте.
   */
  observePopoverResize?: boolean;
  /**
   * ResizeObserver на якоре (триггере). Ложные срабатывания при reflow шапки таблицы
   * дают clamp и сдвиг попапа — для фильтров отключайте, останется window.resize.
   */
  observeTriggerResize?: boolean;
  /**
   * Следовать за якорем при scroll (capture на window). Для фильтра отключайте:
   * reflow/программная прокрутка таблицы даёт ложные события и дёргает fixed-попап.
   */
  repositionOnScroll?: boolean;
  /** Логи [popover-pos] в dev или при localStorage debugPopoverPosition=1 */
  positionDebugTag?: string;
  /**
   * Сколько вложенных requestAnimationFrame перед первым размещением (по умолчанию 3).
   * Для быстрого «переезда» на новый якорь без рывка на старые координаты уменьшите до 1.
   */
  placementRafDepth?: number;
}

function scheduleNestedRafs(depth: number, callback: () => void): void {
  const d = Math.max(1, depth);
  if (d === 1) {
    requestAnimationFrame(callback);
    return;
  }
  requestAnimationFrame(() => scheduleNestedRafs(d - 1, callback));
}

function calcHorizontal(
  align: 'center' | 'start' | 'end',
  triggerRect: DOMRect,
  popoverWidth: number,
): number {
  if (align === 'start') return triggerRect.left;
  if (align === 'end') return triggerRect.right - popoverWidth;
  return triggerRect.left + triggerRect.width / 2 - popoverWidth / 2;
}

function calcVertical(
  align: 'center' | 'start' | 'end',
  triggerRect: DOMRect,
  popoverHeight: number,
): number {
  if (align === 'start') return triggerRect.top;
  if (align === 'end') return triggerRect.bottom - popoverHeight;
  return triggerRect.top + triggerRect.height / 2 - popoverHeight / 2;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

type ScrollLockSnapshot = {
  anchorLeft: number;
  anchorTop: number;
  popoverLeft: number;
  popoverTop: number;
};

/**
 * Универсальный хук для позиционирования поповеров.
 * Принимает либо triggerRef, либо anchorEl (прямую ссылку на элемент).
 * При открытии и при resize — позиция с clamp в пределах viewport.
 * При прокрутке — см. `clampOnScrollFollow` (якорная трансляция с зафиксированным начальным смещением).
 * См. `onAnchorDetached`, если попап монтируется выше строки, а якорь — внутри виртуализированной строки.
 */
export function usePopoverPositioning({
  triggerRef,
  anchorEl,
  popoverRef,
  isOpen,
  preferredPositionX = 'left',
  preferredPositionY = 'bottom',
  horizontalAlign = 'center',
  verticalAlign = 'center',
  offset = 8,
  margin = {},
  onPositionChange,
  dependencies = [],
  priorityAxis = 'y',
  zIndex = 1000,
  verticalOffset = 0,
  clampOnScrollFollow = false,
  onAnchorDetached,
  observePopoverResize = true,
  observeTriggerResize = true,
  repositionOnScroll = true,
  positionDebugTag,
  placementRafDepth = 3,
}: UsePopoverPositioningOptions) {
  const [determinedPosition, setDeterminedPosition] = useState<string | null>(null);
  const scrollLockRef = useRef<ScrollLockSnapshot | null>(null);
  const prevPopoverAnchorRef = useRef<HTMLElement | null>(null);
  const anchorDetachHandledRef = useRef(false);
  const onAnchorDetachedRef = useRef(onAnchorDetached);
  const positionDebugTagRef = useRef<string | undefined>(undefined);
  positionDebugTagRef.current = positionDebugTag;
  useEffect(() => {
    onAnchorDetachedRef.current = onAnchorDetached;
  }, [onAnchorDetached]);

  const stableOnPositionChange = useRef(onPositionChange);
  useEffect(() => {
    stableOnPositionChange.current = onPositionChange;
  }, [onPositionChange]);

  useEffect(() => {
    if (!isOpen) anchorDetachHandledRef.current = false;
  }, [isOpen]);

  const defaultMargin = {
    top: 20,
    bottom: 20,
    left: 20,
    right: 20,
    ...margin,
  };

  /** Возвращает текущий якорный элемент */
  const getTriggerEl = useCallback((): HTMLElement | null => {
    return anchorEl !== undefined ? (anchorEl ?? null) : (triggerRef?.current ?? null);
  }, [anchorEl, triggerRef]);

  const usesTriggerRefOnly = anchorEl === undefined && triggerRef != null;

  const tryReportAnchorDetached = useCallback((): boolean => {
    const cb = onAnchorDetachedRef.current;
    if (!cb || anchorDetachHandledRef.current) return false;
    const t = getTriggerEl();
    if (t != null && !t.isConnected) {
      anchorDetachHandledRef.current = true;
      cb();
      return true;
    }
    if (usesTriggerRefOnly && determinedPosition != null && t == null) {
      anchorDetachHandledRef.current = true;
      cb();
      return true;
    }
    return false;
  }, [getTriggerEl, usesTriggerRefOnly, determinedPosition]);

  const captureScrollLock = useCallback(
    (trigger: HTMLElement, popoverEl: HTMLElement) => {
      if (!clampOnScrollFollow) return;
      const tr = trigger.getBoundingClientRect();
      const pr = popoverEl.getBoundingClientRect();
      scrollLockRef.current = {
        anchorLeft: tr.left,
        anchorTop: tr.top,
        popoverLeft: pr.left,
        popoverTop: pr.top,
      };
    },
    [clampOnScrollFollow],
  );

  const buildPositions = useCallback(
    (triggerRect: DOMRect, popoverWidth: number, popoverHeight: number) => {
      const hPos = calcHorizontal(horizontalAlign, triggerRect, popoverWidth);
      const vPos = calcVertical(verticalAlign, triggerRect, popoverHeight) + verticalOffset;
      return {
        right:  { top: vPos, left: triggerRect.right + offset },
        left:   { top: vPos, left: triggerRect.left - popoverWidth - offset },
        bottom: { top: triggerRect.bottom + offset + verticalOffset, left: hPos },
        top:    { top: triggerRect.top - popoverHeight - offset + verticalOffset, left: hPos },
        center: {
          top:  window.innerHeight / 2 - popoverHeight / 2,
          left: window.innerWidth  / 2 - popoverWidth  / 2,
        },
      };
    },
    [horizontalAlign, verticalAlign, offset, verticalOffset],
  );

  const applyPosition = useCallback(
    (
      popoverEl: HTMLElement,
      posKey: string,
      triggerRect: DOMRect,
      popoverWidth: number,
      popoverHeight: number,
      clampToViewport = true,
    ) => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const positions = buildPositions(triggerRect, popoverWidth, popoverHeight);
      let { left, top } = (positions as Record<string, { top: number; left: number }>)[posKey] ?? { left: 0, top: 0 };

      if (clampToViewport) {
        top  = clamp(top,  defaultMargin.top,    vh - popoverHeight - defaultMargin.bottom);
        left = clamp(left, defaultMargin.left, vw - popoverWidth  - defaultMargin.right);
      }

      popoverEl.style.position = 'fixed';
      popoverEl.style.left     = `${left}px`;
      popoverEl.style.top      = `${top}px`;
      popoverEl.style.zIndex   = String(zIndex);

      if (positionDebugTagRef.current) {
        logPopoverPosition(positionDebugTagRef.current, 'applyPosition', {
          posKey,
          clampToViewport,
          left: Math.round(left),
          top: Math.round(top),
          popoverWidth: Math.round(popoverWidth),
          popoverHeight: Math.round(popoverHeight),
        });
      }

      if (stableOnPositionChange.current) {
        stableOnPositionChange.current({
          position: posKey,
          left,
          top,
          triggerRect,
          popoverRect: { width: popoverWidth, height: popoverHeight },
        });
      }
    },
    [buildPositions, defaultMargin.top, defaultMargin.bottom, defaultMargin.left, defaultMargin.right, zIndex],
  );

  const updatePopoverPosition = useCallback(
    (clampToViewport = true) => {
      if (tryReportAnchorDetached()) return;
      const trigger = getTriggerEl();
      if (!determinedPosition || !trigger || !popoverRef.current) return;
      if (!trigger.isConnected) {
        tryReportAnchorDetached();
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const popoverEl = popoverRef.current;
      const popoverRect = popoverEl.getBoundingClientRect();

      if (positionDebugTagRef.current) {
        logPopoverPosition(positionDebugTagRef.current, 'updatePopoverPosition', {
          clampToViewport,
          popRect: {
            top: Math.round(popoverRect.top),
            left: Math.round(popoverRect.left),
            w: Math.round(popoverRect.width),
            h: Math.round(popoverRect.height),
          },
          trigRectTop: Math.round(triggerRect.top),
        });
      }

      applyPosition(
        popoverEl,
        determinedPosition,
        triggerRect,
        popoverRect.width,
        popoverRect.height,
        clampToViewport,
      );
      if (clampToViewport) {
        captureScrollLock(trigger, popoverEl);
      }
    },
    [determinedPosition, getTriggerEl, popoverRef, applyPosition, captureScrollLock, tryReportAnchorDetached],
  );

  const applyScrollFollowFromLock = useCallback(() => {
    if (tryReportAnchorDetached()) return;
    const trigger = getTriggerEl();
    const popoverEl = popoverRef.current;
    const lock = scrollLockRef.current;
    const pos = determinedPosition;
    if (!trigger || !popoverEl || !lock || !pos) {
      updatePopoverPosition(true);
      return;
    }
    if (!trigger.isConnected) {
      tryReportAnchorDetached();
      return;
    }
    const tr = trigger.getBoundingClientRect();
    const dLeft = tr.left - lock.anchorLeft;
    const dTop = tr.top - lock.anchorTop;
    const left = lock.popoverLeft + dLeft;
    const top = lock.popoverTop + dTop;
    if (positionDebugTagRef.current) {
      logPopoverPosition(positionDebugTagRef.current, 'applyScrollFollowFromLock', {
        left: Math.round(left),
        top: Math.round(top),
        dLeft: Math.round(dLeft),
        dTop: Math.round(dTop),
      });
    }
    popoverEl.style.position = 'fixed';
    popoverEl.style.left = `${left}px`;
    popoverEl.style.top = `${top}px`;
    popoverEl.style.zIndex = String(zIndex);
    const pr = popoverEl.getBoundingClientRect();
    if (stableOnPositionChange.current) {
      stableOnPositionChange.current({
        position: pos,
        left,
        top,
        triggerRect: tr,
        popoverRect: { width: pr.width, height: pr.height },
      });
    }
  }, [determinedPosition, getTriggerEl, popoverRef, updatePopoverPosition, zIndex, tryReportAnchorDetached]);

  // Начальное определение позиции при открытии
  useEffect(() => {
    if (!isOpen) {
      setDeterminedPosition(null);
      scrollLockRef.current = null;
      prevPopoverAnchorRef.current = null;
      return;
    }

    if (!popoverRef.current) return;
    if (tryReportAnchorDetached()) return;
    const trigger = getTriggerEl();
    if (!trigger) return;

    const anchorChangedForPopover =
      prevPopoverAnchorRef.current != null && prevPopoverAnchorRef.current !== trigger;
    prevPopoverAnchorRef.current = trigger;
    if (anchorChangedForPopover) {
      const popEl = popoverRef.current;
      popEl.style.left = '-9999px';
      popEl.style.top = '-9999px';
      setDeterminedPosition(null);
      scrollLockRef.current = null;
    }

    const runInitialPosition = () => {
      if (tryReportAnchorDetached()) return;
      const el = popoverRef.current;
      const triggerNow = getTriggerEl();
      if (!el || !triggerNow) return;
      if (!triggerNow.isConnected) {
        tryReportAnchorDetached();
        return;
      }
      void el.offsetHeight;
      const triggerRect  = triggerNow.getBoundingClientRect();
      const popoverRect  = el.getBoundingClientRect();
      const popoverWidth  = popoverRect.width;
      const popoverHeight = popoverRect.height;

      if (popoverWidth < 1 || popoverHeight < 1) {
        setDeterminedPosition('bottom');
        return;
      }

      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const hasSpace = {
        left:   triggerRect.left >= popoverWidth  + offset,
        right:  vw - triggerRect.right >= popoverWidth  + offset,
        top:    triggerRect.top  >= popoverHeight + offset,
        bottom: vh - triggerRect.bottom >= popoverHeight + offset,
      };

      const xOrder: ('left' | 'right')[] = preferredPositionX === 'left' ? ['left', 'right'] : ['right', 'left'];
      const yOrder: ('top' | 'bottom')[] = preferredPositionY === 'top'  ? ['top', 'bottom']  : ['bottom', 'top'];

      const finalPosition =
        priorityAxis === 'x'
          ? (xOrder.find((p) => hasSpace[p]) ?? yOrder.find((p) => hasSpace[p]) ?? 'center')
          : (yOrder.find((p) => hasSpace[p]) ?? xOrder.find((p) => hasSpace[p]) ?? 'center');

      setDeterminedPosition(finalPosition);
      if (positionDebugTagRef.current) {
        logPopoverPosition(positionDebugTagRef.current, 'initial placement', {
          finalPosition,
          popW: Math.round(popoverWidth),
          popH: Math.round(popoverHeight),
        });
      }
      applyPosition(el, finalPosition, triggerRect, popoverWidth, popoverHeight, true);
      captureScrollLock(triggerNow, el);
    };

    scheduleNestedRafs(placementRafDepth, runInitialPosition);
  }, [
    isOpen,
    preferredPositionX,
    preferredPositionY,
    offset,
    getTriggerEl,
    popoverRef,
    priorityAxis,
    applyPosition,
    captureScrollLock,
    tryReportAnchorDetached,
    placementRafDepth,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...dependencies,
  ]);

  useEffect(() => {
    if (!isOpen || !onAnchorDetached) return;
    let rafId = 0;
    let alive = true;
    const loop = () => {
      if (!alive) return;
      tryReportAnchorDetached();
      if (!anchorDetachHandledRef.current) {
        rafId = window.requestAnimationFrame(loop);
      }
    };
    rafId = window.requestAnimationFrame(loop);
    return () => {
      alive = false;
      window.cancelAnimationFrame(rafId);
    };
  }, [isOpen, onAnchorDetached, tryReportAnchorDetached]);

  // Scroll — только следование за якорем (без clamp). Resize / смена размеров — с clamp.
  useEffect(() => {
    if (!isOpen || !determinedPosition || !popoverRef.current) return;

    let tickingScroll = false;
    let tickingClamp = false;

    const scheduleScrollFollow = () => {
      if (!tickingScroll) {
        tickingScroll = true;
        window.requestAnimationFrame(() => {
          if (clampOnScrollFollow) {
            applyScrollFollowFromLock();
          } else {
            updatePopoverPosition(false);
          }
          tickingScroll = false;
        });
      }
    };

    const scheduleClamp = () => {
      if (positionDebugTagRef.current) {
        logPopoverPosition(positionDebugTagRef.current, 'scheduleClamp (window.resize | ResizeObserver)');
      }
      if (!tickingClamp) {
        tickingClamp = true;
        window.requestAnimationFrame(() => {
          updatePopoverPosition(true);
          tickingClamp = false;
        });
      }
    };

    const handleScroll = (e: Event) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if ((e.target as Element)?.closest?.('[data-combo-dropdown]')) return;
      if (positionDebugTagRef.current) {
        const t = e.target;
        logPopoverPosition(positionDebugTagRef.current, 'capture scroll вне попапа → follow', {
          target:
            t instanceof Element
              ? `${t.tagName}.${String(t.className ?? '').slice(0, 96)}`
              : String(t),
        });
      }
      scheduleScrollFollow();
    };

    if (repositionOnScroll) {
      window.addEventListener('scroll', handleScroll, true);
    }
    window.addEventListener('resize', scheduleClamp);

    const resizeObserver = new ResizeObserver((entries) => {
      if (positionDebugTagRef.current) {
        for (const entry of entries) {
          const t = entry.target as HTMLElement;
          logPopoverPosition(positionDebugTagRef.current, 'ResizeObserver entry', {
            tag: t.tagName,
            cls: String(t.className ?? '').slice(0, 120),
            contentW: Math.round(entry.contentRect.width),
            contentH: Math.round(entry.contentRect.height),
          });
        }
      }
      scheduleClamp();
    });
    if (observeTriggerResize) {
      const trigger = getTriggerEl();
      if (trigger) resizeObserver.observe(trigger);
    }
    if (observePopoverResize && popoverRef.current) resizeObserver.observe(popoverRef.current);

    return () => {
      if (repositionOnScroll) {
        window.removeEventListener('scroll', handleScroll, true);
      }
      window.removeEventListener('resize', scheduleClamp);
      resizeObserver.disconnect();
    };
  }, [
    isOpen,
    determinedPosition,
    updatePopoverPosition,
    applyScrollFollowFromLock,
    popoverRef,
    getTriggerEl,
    clampOnScrollFollow,
    observePopoverResize,
    observeTriggerResize,
    repositionOnScroll,
  ]);

  const repositionPopover = useCallback(() => updatePopoverPosition(true), [updatePopoverPosition]);

  return { repositionPopover };
}
